package ui.user.detail

import common.frontend.api.*
import common.frontend.api.Io.Permission.Companion.NONE
import common.frontend.api.Io.Permission.Companion.OTHERS_ADD
import common.frontend.api.Io.Permission.Companion.OTHERS_DELETE
import common.frontend.api.Io.Permission.Companion.OTHERS_EDIT
import common.frontend.api.Io.Permission.Companion.OTHERS_VIEW
import common.frontend.api.Io.Permission.Companion.OWN_ADD
import common.frontend.api.Io.Permission.Companion.OWN_DELETE
import common.frontend.api.Io.Permission.Companion.OWN_EDIT
import common.frontend.api.Io.Permission.Companion.OWN_VIEW
import org.jetbrains.compose.resources.StringResource
import ssoft.frontend.generated.resources.*
import ui.user.detail.UserScope.WrapperPermissionItem

data class PermissionGroup(
    val label: StringResource,
    private val permissions: List<Pair<WrapperPermissionItem, Int>>
) {
    fun activate() {
        permissions.forEach {
            it.first.applyGroupChanges(it.second)
        }
    }
}


private fun buildPermissionEntry(itemWrapper: WrapperPermissionItem, expected: Int): Pair<WrapperPermissionItem, Int> {
    var result = 0
    Io.Permission.all.forEach {
        if ((itemWrapper.loginPermission and it) > 0 && (expected and it > 0)) {
            result += it
        }
    }
    return itemWrapper to (result.takeIf { it != 0 } ?: NONE)
}

fun UserScope.WrapperPermission.rebuildPermissionGroups(login: Io.User, targetUser: Io.User): List<PermissionGroup> {
    return buildList {
        if (login.canUserPermissionEdit(targetUser.company.id)) {
            if (login.isSuperAdmin()) {
                add(superAdmin(Res.string.user_detail__permission_group_super_admin))
            }

            if (login.isServerAdmin()) {
                add(serverAdmin(Res.string.user_detail__permission_group_server_admin))
            }

            if (login.isCompanyAdmin()) {
                add(companyAdmin(Res.string.user_detail__permission_group_company_admin))
            }

            if (login.isAdCompanyPublisher()) {
                add(adCompanyPublisher(Res.string.user_detail__permission_group_publisher_ad_company))
            }

            if (login.isAdServerPublisher()) {
                add(adServerPublisher(Res.string.user_detail__permission_group_publisher_ad_server))
            }
        }
    }

}

private fun UserScope.WrapperPermission.superAdmin(label: StringResource): PermissionGroup {
    return PermissionGroup(
        label = label,
        permissions = listOf(
            buildPermissionEntry(
                itemWrapper = user,
                expected = OWN_VIEW + OWN_ADD + OWN_EDIT + OWN_DELETE
                    + OTHERS_VIEW + OTHERS_ADD + OTHERS_EDIT + OTHERS_DELETE
            ),
            buildPermissionEntry(
                itemWrapper = userPermission,
                expected = OWN_VIEW + OWN_EDIT
                    + OTHERS_VIEW + OTHERS_EDIT
            ),
            buildPermissionEntry(
                itemWrapper = company,
                expected = OWN_VIEW + OWN_EDIT
                    + OTHERS_VIEW + OTHERS_ADD + OTHERS_EDIT + OTHERS_DELETE
            ),
            buildPermissionEntry(
                itemWrapper = location,
                expected = OWN_VIEW + OWN_ADD + OWN_EDIT + OWN_DELETE
                    + OTHERS_VIEW + OTHERS_ADD + OTHERS_EDIT + OTHERS_DELETE
            ),
            buildPermissionEntry(
                itemWrapper = locationEmergency,
                expected = OWN_VIEW + OWN_ADD + OWN_EDIT
                    + OTHERS_VIEW + OTHERS_ADD + OTHERS_EDIT
            ),
            buildPermissionEntry(
                itemWrapper = deviceInfo,
                expected = OWN_VIEW + OWN_ADD + OWN_EDIT + OWN_DELETE
                    + OTHERS_VIEW + OTHERS_ADD + OTHERS_EDIT + OTHERS_DELETE
            ),
            buildPermissionEntry(
                itemWrapper = deviceDisplay,
                expected = OWN_VIEW + OWN_EDIT
                    + OTHERS_VIEW + OTHERS_EDIT
            ),
            buildPermissionEntry(
                itemWrapper = deviceMode,
                expected = OWN_VIEW + OWN_ADD + OWN_EDIT + OWN_DELETE
                    + OTHERS_VIEW + OTHERS_ADD + OTHERS_EDIT + OTHERS_DELETE
            ),
            buildPermissionEntry(
                itemWrapper = server,
                expected = OWN_VIEW + OWN_EDIT
                    + OTHERS_VIEW + OTHERS_ADD + OTHERS_EDIT
            ),
            buildPermissionEntry(
                itemWrapper = deviceActions,
                expected = OWN_VIEW
                    + OTHERS_VIEW
            ),
            buildPermissionEntry(
                itemWrapper = resources,
                expected = OWN_VIEW + OWN_ADD + OWN_EDIT + OWN_DELETE
                    + OTHERS_VIEW + OTHERS_ADD + OTHERS_EDIT + OTHERS_DELETE
            ),
            buildPermissionEntry(
                itemWrapper = campaign,
                expected = OWN_VIEW + OWN_ADD + OWN_EDIT + OWN_DELETE
                    + OTHERS_VIEW + OTHERS_ADD + OTHERS_EDIT + OTHERS_DELETE
            ),
            buildPermissionEntry(
                itemWrapper = deviceShare,
                expected = OWN_VIEW + OWN_ADD + OWN_EDIT + OWN_DELETE
                    + OTHERS_VIEW + OTHERS_ADD + OTHERS_EDIT + OTHERS_DELETE
            ),
            buildPermissionEntry(
                itemWrapper = translation,
                expected = OWN_VIEW + OWN_EDIT
            ),
            buildPermissionEntry(
                itemWrapper = reportSystem,
                expected = OWN_VIEW
                    + OTHERS_VIEW
            ),
            buildPermissionEntry(
                itemWrapper = reportLayerTop,
                expected = OWN_VIEW
                    + OTHERS_VIEW
            ),
            buildPermissionEntry(
                itemWrapper = reportLayerBottom,
                expected = OWN_VIEW
                    + OTHERS_VIEW
            ),
            buildPermissionEntry(
                itemWrapper = license,
                expected = OWN_VIEW + OWN_ADD + OWN_EDIT + OWN_DELETE
                    + OTHERS_VIEW + OTHERS_ADD + OTHERS_EDIT + OTHERS_DELETE
            ),
            buildPermissionEntry(
                itemWrapper = hardware,
                expected = OWN_VIEW + OWN_ADD + OWN_EDIT + OTHERS_VIEW
            ),
        )
    )
}

private fun UserScope.WrapperPermission.serverAdmin(label: StringResource): PermissionGroup {
    return PermissionGroup(
        label = label,
        permissions = listOf(
            buildPermissionEntry(
                itemWrapper = user,
                expected = OWN_VIEW + OWN_ADD + OWN_EDIT + OWN_DELETE
                    + OTHERS_VIEW + OTHERS_ADD + OTHERS_EDIT + OTHERS_DELETE
            ),
            buildPermissionEntry(
                itemWrapper = userPermission,
                expected = OWN_VIEW + OWN_EDIT
                    + OTHERS_VIEW + OTHERS_EDIT
            ),
            buildPermissionEntry(
                itemWrapper = company,
                expected = OWN_VIEW + OWN_EDIT
                    + OTHERS_VIEW + OTHERS_ADD + OTHERS_EDIT + OTHERS_DELETE
            ),
            buildPermissionEntry(
                itemWrapper = location,
                expected = OWN_VIEW + OWN_ADD + OWN_EDIT + OWN_DELETE
                    + OTHERS_VIEW + OTHERS_ADD + OTHERS_EDIT + OTHERS_DELETE
            ),
            buildPermissionEntry(
                itemWrapper = locationEmergency,
                expected = OWN_VIEW + OWN_ADD + OWN_EDIT
                    + OTHERS_VIEW + OTHERS_ADD + OTHERS_EDIT
            ),
            buildPermissionEntry(
                itemWrapper = deviceInfo,
                expected = OWN_VIEW + OWN_ADD + OWN_EDIT + OWN_DELETE
                    + OTHERS_VIEW + OTHERS_ADD + OTHERS_EDIT + OTHERS_DELETE
            ),
            buildPermissionEntry(
                itemWrapper = deviceDisplay,
                expected = OWN_VIEW + OWN_EDIT
                    + OTHERS_VIEW + OTHERS_EDIT
            ),
            buildPermissionEntry(
                itemWrapper = deviceMode,
                expected = OWN_VIEW + OWN_ADD + OWN_EDIT + OWN_DELETE
                    + OTHERS_VIEW + OTHERS_ADD + OTHERS_EDIT + OTHERS_DELETE
            ),
            buildPermissionEntry(
                itemWrapper = server,
                expected = NONE
            ),
            buildPermissionEntry(
                itemWrapper = deviceActions,
                expected = OWN_VIEW
                    + OTHERS_VIEW
            ),
            buildPermissionEntry(
                itemWrapper = resources,
                expected = OWN_VIEW + OWN_ADD + OWN_EDIT + OWN_DELETE
                    + OTHERS_VIEW + OTHERS_ADD + OTHERS_EDIT + OTHERS_DELETE
            ),
            buildPermissionEntry(
                itemWrapper = campaign,
                expected = OWN_VIEW + OWN_ADD + OWN_EDIT + OWN_DELETE
                    + OTHERS_VIEW + OTHERS_ADD + OTHERS_EDIT + OTHERS_DELETE
            ),
            buildPermissionEntry(
                itemWrapper = deviceShare,
                expected = OWN_VIEW + OWN_ADD + OWN_EDIT + OWN_DELETE
                    + OTHERS_VIEW + OTHERS_ADD + OTHERS_EDIT + OTHERS_DELETE
            ),
            buildPermissionEntry(
                itemWrapper = translation,
                expected = NONE
            ),
            buildPermissionEntry(
                itemWrapper = reportSystem,
                expected = OWN_VIEW
                    + OTHERS_VIEW
            ),
            buildPermissionEntry(
                itemWrapper = reportLayerTop,
                expected = OWN_VIEW
                    + OTHERS_VIEW
            ),
            buildPermissionEntry(
                itemWrapper = reportLayerBottom,
                expected = OWN_VIEW
                    + OTHERS_VIEW
            ),
            buildPermissionEntry(
                itemWrapper = license,
                expected = OWN_VIEW + OWN_ADD + OWN_DELETE
                    + OTHERS_VIEW + OTHERS_ADD + OTHERS_DELETE
            ),
            buildPermissionEntry(
                itemWrapper = hardware,
                expected = OWN_VIEW + OTHERS_VIEW
            ),
        )
    )
}

private fun UserScope.WrapperPermission.companyAdmin(label: StringResource): PermissionGroup {
    return PermissionGroup(
        label = label,
        permissions = listOf(
            buildPermissionEntry(
                itemWrapper = user,
                expected = OWN_VIEW + OWN_ADD + OWN_EDIT + OWN_DELETE
            ),
            buildPermissionEntry(
                itemWrapper = userPermission,
                expected = OWN_VIEW + OWN_EDIT
            ),
            buildPermissionEntry(
                itemWrapper = company,
                expected = OWN_VIEW + OWN_EDIT
            ),
            buildPermissionEntry(
                itemWrapper = location,
                expected = OWN_VIEW + OWN_ADD + OWN_EDIT + OWN_DELETE
            ),
            buildPermissionEntry(
                itemWrapper = locationEmergency,
                expected = OWN_VIEW + OWN_ADD + OWN_EDIT
            ),
            buildPermissionEntry(
                itemWrapper = deviceInfo,
                expected = OWN_VIEW + OWN_ADD + OWN_EDIT
            ),
            buildPermissionEntry(
                itemWrapper = deviceDisplay,
                expected = OWN_VIEW + OWN_EDIT
            ),
            buildPermissionEntry(
                itemWrapper = deviceMode,
                expected = OWN_VIEW + OWN_ADD + OWN_EDIT
            ),
            buildPermissionEntry(
                itemWrapper = server,
                expected = NONE
            ),
            buildPermissionEntry(
                itemWrapper = deviceActions,
                expected = OWN_VIEW
            ),
            buildPermissionEntry(
                itemWrapper = resources,
                expected = OWN_VIEW + OWN_ADD + OWN_EDIT + OWN_DELETE
            ),
            buildPermissionEntry(
                itemWrapper = campaign,
                expected = OWN_VIEW + OWN_ADD + OWN_EDIT + OWN_DELETE
            ),
            buildPermissionEntry(
                itemWrapper = deviceShare,
                expected = OWN_VIEW + OWN_ADD + OWN_EDIT
            ),
            buildPermissionEntry(
                itemWrapper = translation,
                expected = NONE
            ),
            buildPermissionEntry(
                itemWrapper = reportSystem,
                expected = OWN_VIEW
            ),
            buildPermissionEntry(
                itemWrapper = reportLayerTop,
                expected = OWN_VIEW
            ),
            buildPermissionEntry(
                itemWrapper = reportLayerBottom,
                expected = OWN_VIEW
            ),
            buildPermissionEntry(
                itemWrapper = license,
                expected = NONE
            ),

            buildPermissionEntry(
                itemWrapper = hardware,
                expected = OWN_VIEW
            ),
        )
    )
}

private fun UserScope.WrapperPermission.adCompanyPublisher(label: StringResource): PermissionGroup {
    return PermissionGroup(
        label = label,
        permissions = listOf(
            buildPermissionEntry(
                itemWrapper = user,
                expected = NONE
            ),
            buildPermissionEntry(
                itemWrapper = userPermission,
                expected = NONE
            ),
            buildPermissionEntry(
                itemWrapper = company,
                expected = NONE
            ),
            buildPermissionEntry(
                itemWrapper = location,
                expected = OWN_VIEW
            ),
            buildPermissionEntry(
                itemWrapper = locationEmergency,
                expected = NONE
            ),
            buildPermissionEntry(
                itemWrapper = deviceInfo,
                expected = OWN_VIEW
            ),
            buildPermissionEntry(
                itemWrapper = deviceDisplay,
                expected = NONE
            ),
            buildPermissionEntry(
                itemWrapper = deviceMode,
                expected = NONE
            ),
            buildPermissionEntry(
                itemWrapper = server,
                expected = NONE
            ),
            buildPermissionEntry(
                itemWrapper = deviceActions,
                expected = NONE
            ),
            buildPermissionEntry(
                itemWrapper = resources,
                expected = OWN_VIEW + OWN_ADD + OWN_EDIT + OWN_DELETE
            ),
            buildPermissionEntry(
                itemWrapper = campaign,
                expected = OWN_VIEW + OWN_ADD + OWN_EDIT + OWN_DELETE
            ),
            buildPermissionEntry(
                itemWrapper = deviceShare,
                expected = NONE
            ),
            buildPermissionEntry(
                itemWrapper = translation,
                expected = NONE
            ),
            buildPermissionEntry(
                itemWrapper = reportSystem,
                expected = OWN_VIEW
            ),
            buildPermissionEntry(
                itemWrapper = reportLayerTop,
                expected = OWN_VIEW
            ),
            buildPermissionEntry(
                itemWrapper = reportLayerBottom,
                expected = NONE
            ),
            buildPermissionEntry(
                itemWrapper = license,
                expected = NONE
            ),
            buildPermissionEntry(
                itemWrapper = hardware,
                expected = NONE
            ),
        )
    )
}

private fun UserScope.WrapperPermission.adServerPublisher(label: StringResource): PermissionGroup {
    return PermissionGroup(
        label = label,
        permissions = listOf(
            buildPermissionEntry(
                itemWrapper = user,
                expected = NONE
            ),
            buildPermissionEntry(
                itemWrapper = userPermission,
                expected = NONE
            ),
            buildPermissionEntry(
                itemWrapper = company,
                expected = OWN_VIEW
                    + OTHERS_VIEW
            ),
            buildPermissionEntry(
                itemWrapper = location,
                expected = OWN_VIEW
                    + OTHERS_VIEW
            ),
            buildPermissionEntry(
                itemWrapper = locationEmergency,
                expected = NONE
            ),
            buildPermissionEntry(
                itemWrapper = deviceInfo,
                expected = OWN_VIEW
                    + OTHERS_VIEW
            ),
            buildPermissionEntry(
                itemWrapper = deviceDisplay,
                expected = NONE
            ),
            buildPermissionEntry(
                itemWrapper = deviceMode,
                expected = NONE
            ),
            buildPermissionEntry(
                itemWrapper = server,
                expected = NONE
            ),
            buildPermissionEntry(
                itemWrapper = deviceActions,
                expected = NONE
            ),
            buildPermissionEntry(
                itemWrapper = resources,
                expected = OWN_VIEW + OWN_ADD + OWN_EDIT + OWN_DELETE
                    + OTHERS_VIEW + OTHERS_ADD + OTHERS_EDIT + OTHERS_DELETE
            ),
            buildPermissionEntry(
                itemWrapper = campaign,
                expected = OWN_VIEW + OWN_ADD + OWN_EDIT + OWN_DELETE
                    + OTHERS_VIEW + OTHERS_ADD + OTHERS_EDIT + OTHERS_DELETE
            ),
            buildPermissionEntry(
                itemWrapper = deviceShare,
                expected = NONE
            ),
            buildPermissionEntry(
                itemWrapper = translation,
                expected = NONE
            ),
            buildPermissionEntry(
                itemWrapper = reportSystem,
                expected = NONE
            ),
            buildPermissionEntry(
                itemWrapper = reportLayerTop,
                expected = OWN_VIEW
                    + OTHERS_VIEW
            ),
            buildPermissionEntry(
                itemWrapper = reportLayerBottom,
                expected = NONE
            ),
            buildPermissionEntry(
                itemWrapper = license,
                expected = NONE
            ),
            buildPermissionEntry(
                itemWrapper = hardware,
                expected = NONE
            ),
        )
    )
}
